import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  TrendingDown,
  TrendingUp,
  Repeat,
  ArrowRight,
  Home,
  Zap,
  Film,
  Music,
  Wifi,
  Phone,
  Car,
  GraduationCap,
  Shield,
  Plus,
} from 'lucide-react';
import { useCurrency } from '@/contexts';
import { useTranslation } from '@/hooks/useTranslation';
import type { Database } from '@/lib/database.types';

type RecurringTransaction = Database['public']['Tables']['recurring_transactions']['Row'] & {
  category?: Database['public']['Tables']['categories']['Row'] | null;
  account?: Database['public']['Tables']['accounts']['Row'] | null;
};

interface UpcomingItem {
  recurring: RecurringTransaction;
  date: Date;
}

interface UpcomingBillsProps {
  upcomingItems: UpcomingItem[];
  daysAhead?: number;
  onAddRecurring?: () => void;
  isLoading?: boolean;
}

// Icon mapping for common recurring items
const getRecurringIcon = (description: string, categoryName?: string | null): React.ElementType => {
  const desc = description.toLowerCase();
  const cat = categoryName?.toLowerCase() || '';

  if (
    desc.includes('netflix') ||
    desc.includes('disney') ||
    desc.includes('prime') ||
    cat.includes('streaming')
  )
    return Film;
  if (desc.includes('spotify') || desc.includes('deezer') || desc.includes('music')) return Music;
  if (desc.includes('internet') || desc.includes('fibre') || desc.includes('wifi')) return Wifi;
  if (
    desc.includes('téléphone') ||
    desc.includes('phone') ||
    desc.includes('mobile') ||
    desc.includes('forfait')
  )
    return Phone;
  if (
    desc.includes('loyer') ||
    desc.includes('rent') ||
    cat.includes('housing') ||
    cat.includes('logement')
  )
    return Home;
  if (
    desc.includes('électricité') ||
    desc.includes('edf') ||
    desc.includes('engie') ||
    cat.includes('utilities')
  )
    return Zap;
  if (desc.includes('assurance') || desc.includes('insurance') || cat.includes('insurance'))
    return Shield;
  if (desc.includes('voiture') || desc.includes('car') || cat.includes('transport')) return Car;
  if (desc.includes('école') || desc.includes('formation') || cat.includes('education'))
    return GraduationCap;

  return Repeat;
};

export function UpcomingBills({
  upcomingItems,
  onAddRecurring,
  isLoading = false,
}: UpcomingBillsProps) {
  const navigate = useNavigate();
  const { formatAmount, isSecretMode } = useCurrency();
  const { t, language } = useTranslation();

  // Filter and group by time range
  const { next7Days, next30Days, totalAmount, expenseAmount, incomeAmount } = useMemo(() => {
    const now = new Date();
    const in7Days = new Date();
    in7Days.setDate(now.getDate() + 7);
    const in30Days = new Date();
    in30Days.setDate(now.getDate() + 30);

    const next7: UpcomingItem[] = [];
    const next30: UpcomingItem[] = [];
    let total = 0;
    let expenses = 0;
    let income = 0;

    for (const item of upcomingItems) {
      if (item.date <= in7Days) {
        next7.push(item);
      } else if (item.date <= in30Days) {
        next30.push(item);
      }

      if (item.date <= in30Days) {
        const amount = item.recurring.amount;
        total += amount;
        if (item.recurring.type === 'expense') {
          expenses += amount;
        } else {
          income += amount;
        }
      }
    }

    return {
      next7Days: next7.slice(0, 5),
      next30Days: next30.slice(0, 3),
      totalAmount: total,
      expenseAmount: expenses,
      incomeAmount: income,
    };
  }, [upcomingItems]);

  const formatDate = (date: Date) => {
    const now = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(now.getDate() + 1);

    if (date.toDateString() === now.toDateString()) {
      return t('dashboard.recurring.today');
    }
    if (date.toDateString() === tomorrow.toDateString()) {
      return t('dashboard.recurring.tomorrow');
    }
    return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
      day: 'numeric',
      month: 'short',
    });
  };

  const getDaysUntil = (date: Date) => {
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-5 animate-pulse">
        <div className="flex items-center justify-between mb-4">
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-32" />
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16" />
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg" />
              <div className="flex-1">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-1" />
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16" />
              </div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-400" />
          <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.1em]">
            {t('dashboard.recurring.title') || 'Factures à venir'}
          </p>
        </div>
        {onAddRecurring && (
          <button
            onClick={onAddRecurring}
            className="text-xs text-[#16c47f] hover:text-[#12b070] font-medium flex items-center gap-1"
          >
            <Plus className="w-3 h-3" />
            {t('dashboard.recurring.addButton')}
          </button>
        )}
      </div>

      {upcomingItems.length === 0 ? (
        <div className="text-center py-8">
          <div className="w-14 h-14 bg-gray-100 dark:bg-gray-700 rounded-2xl flex items-center justify-center mx-auto mb-3">
            <Calendar className="w-7 h-7 text-gray-400" />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
            {t('dashboard.recurring.empty')}
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500">
            {t('dashboard.recurring.emptyDesc')}
          </p>
        </div>
      ) : (
        <>
          {/* Summary Card */}
          <div className="bg-gradient-to-r from-gray-50 to-gray-100/50 dark:from-gray-700/30 dark:to-gray-700/10 rounded-xl p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {t('dashboard.recurring.total30Days')}
              </span>
              <span className="text-lg font-bold text-gray-900 dark:text-white tabular-nums">
                {isSecretMode ? '•••' : formatAmount(totalAmount)}
              </span>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-1.5">
                <TrendingDown className="w-3.5 h-3.5 text-[#78909C]" />
                <span className="text-gray-500 dark:text-gray-400">
                  {t('dashboard.recurring.expenses')}
                </span>
                <span className="font-semibold text-[#78909C]">
                  {isSecretMode ? '•••' : formatAmount(expenseAmount)}
                </span>
              </div>
              {incomeAmount > 0 && (
                <div className="flex items-center gap-1.5">
                  <TrendingUp className="w-3.5 h-3.5 text-[#16c47f]" />
                  <span className="text-gray-500 dark:text-gray-400">
                    {t('dashboard.recurring.income')}
                  </span>
                  <span className="font-semibold text-[#16c47f]">
                    {isSecretMode ? '•••' : formatAmount(incomeAmount)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Next 7 Days */}
          {next7Days.length > 0 && (
            <div className="mb-4">
              <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.08em] mb-2 px-1">
                {t('dashboard.recurring.next7Days')}
              </p>
              <div className="space-y-2">
                {next7Days.map((item, index) => {
                  const Icon = getRecurringIcon(
                    item.recurring.description,
                    item.recurring.category?.name
                  );
                  const isExpense = item.recurring.type === 'expense';
                  const daysUntil = getDaysUntil(item.date);
                  const isUrgent = daysUntil <= 2;

                  return (
                    <div
                      key={`${item.recurring.id}-${index}`}
                      className={`flex items-center gap-3 p-2.5 rounded-xl transition-colors ${
                        isUrgent
                          ? 'bg-orange-50/80 dark:bg-orange-900/10 border border-orange-100 dark:border-orange-900/20'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'
                      }`}
                    >
                      <div
                        className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          isExpense
                            ? 'bg-[#78909C]/10 dark:bg-[#78909C]/20'
                            : 'bg-green-50 dark:bg-green-900/20'
                        }`}
                      >
                        <Icon
                          className={`w-4 h-4 ${isExpense ? 'text-[#78909C]' : 'text-[#16c47f]'}`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                          {item.recurring.description}
                        </p>
                        <p
                          className={`text-[11px] ${isUrgent ? 'text-orange-500 font-medium' : 'text-gray-400 dark:text-gray-500'}`}
                        >
                          {formatDate(item.date)}
                        </p>
                      </div>
                      <span
                        className={`text-sm font-bold tabular-nums ${
                          isExpense ? 'text-[#78909C]' : 'text-[#16c47f]'
                        }`}
                      >
                        {isExpense ? '-' : '+'}
                        {isSecretMode ? '•••' : formatAmount(item.recurring.amount)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Later */}
          {next30Days.length > 0 && (
            <div className="pt-3 border-t border-gray-100 dark:border-gray-700/50">
              <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.08em] mb-2 px-1">
                {t('dashboard.recurring.later')}
              </p>
              <div className="space-y-2">
                {next30Days.map((item, index) => {
                  const Icon = getRecurringIcon(
                    item.recurring.description,
                    item.recurring.category?.name
                  );
                  const isExpense = item.recurring.type === 'expense';

                  return (
                    <div
                      key={`later-${item.recurring.id}-${index}`}
                      className="flex items-center gap-3 p-2 rounded-xl opacity-70 hover:opacity-100 transition-opacity"
                    >
                      <div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          isExpense
                            ? 'bg-gray-100 dark:bg-gray-700'
                            : 'bg-green-50/50 dark:bg-green-900/10'
                        }`}
                      >
                        <Icon
                          className={`w-3.5 h-3.5 ${
                            isExpense ? 'text-gray-400' : 'text-[#16c47f]/70'
                          }`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                          {item.recurring.description}
                        </p>
                        <p className="text-[10px] text-gray-400 dark:text-gray-500">
                          {formatDate(item.date)}
                        </p>
                      </div>
                      <span
                        className={`text-xs font-semibold tabular-nums ${
                          isExpense ? 'text-gray-400' : 'text-[#16c47f]/70'
                        }`}
                      >
                        {isSecretMode ? '•••' : formatAmount(item.recurring.amount)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* View All Link */}
          {upcomingItems.length > 8 && (
            <button
              onClick={() => navigate('/calendar')}
              className="w-full mt-4 py-2.5 text-xs font-medium text-[#16c47f] hover:text-[#12b070] flex items-center justify-center gap-1.5 bg-[#16c47f]/5 hover:bg-[#16c47f]/10 rounded-lg transition-colors"
            >
              {t('dashboard.recurring.viewCalendar')}
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </>
      )}
    </div>
  );
}
